import { useState, useEffect } from 'react'
import {
  FolderPlus,
  Sparkles,
  Check,
  X,
  AlertCircle,
  ChevronDown,
  Pencil,
  Save,
  Image as ImageIcon
} from 'lucide-react'
import {
  categoryManagerApi,
  CategoryListItem,
  CategoryJobStatusResponse
} from '@/lib/api'
import { OpenAICostDisplay } from '@/components/OpenAICostDisplay'

interface ProgressState {
  step: number
  total: number
  message: string
  percentage: number
}

export default function CategoryCreatorPage() {
  // Form state
  const [categoryName, setCategoryName] = useState('')
  const [description, setDescription] = useState('')
  const [instructions, setInstructions] = useState('')
  const [selectedParentId, setSelectedParentId] = useState<number>(2) // Default to "Inicio"
  const [generateImage, setGenerateImage] = useState(true)
  const [aiModel, setAiModel] = useState<string>('gpt-4o-mini')

  // Categories list
  const [categories, setCategories] = useState<CategoryListItem[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState<ProgressState | null>(null)
  const [previewData, setPreviewData] = useState<CategoryJobStatusResponse['result'] | null>(null)
  const [generationError, setGenerationError] = useState<string | null>(null)

  // Apply state
  const [isApplying, setIsApplying] = useState(false)
  const [applyProgress, setApplyProgress] = useState<string | null>(null)
  const [applyResult, setApplyResult] = useState<{
    success: boolean
    message: string
    category_id?: number
  } | null>(null)

  // Editing state for preview
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})

  // Load categories on mount
  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setLoadingCategories(true)
      const data = await categoryManagerApi.listCategories()
      setCategories(data.categories)
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoadingCategories(false)
    }
  }

  // Generate category with AI
  const handleGenerate = async () => {
    if (!categoryName.trim() || !description.trim()) return

    setIsGenerating(true)
    setProgress(null)
    setPreviewData(null)
    setGenerationError(null)
    setEditedValues({})

    try {
      // Start the job
      const startResponse = await categoryManagerApi.startCreate({
        name: categoryName.trim(),
        description: description.trim(),
        instructions: instructions.trim() || undefined,
        id_parent: selectedParentId,
        model: aiModel,
        generate_image: generateImage
      })

      const jobId = startResponse.job_id

      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const status = await categoryManagerApi.getCreateStatus(jobId)

          if (status.progress) {
            setProgress({
              step: status.progress.step,
              total: status.progress.total,
              message: status.progress.message,
              percentage: status.progress.percentage
            })
          }

          if (status.status === 'completed' && status.result) {
            clearInterval(pollInterval)
            setPreviewData(status.result)
            setIsGenerating(false)
          } else if (status.status === 'failed') {
            clearInterval(pollInterval)
            setGenerationError(status.error || 'Error desconocido')
            setIsGenerating(false)
          }
        } catch (error) {
          clearInterval(pollInterval)
          setGenerationError('Error al consultar el estado del trabajo')
          setIsGenerating(false)
        }
      }, 1000)

    } catch (error: any) {
      setGenerationError(error.response?.data?.detail || 'Error al iniciar la generación')
      setIsGenerating(false)
    }
  }

  // Apply changes (create category)
  const handleApply = async () => {
    if (!previewData?.optimized) return

    setIsApplying(true)
    setApplyProgress('Preparando traducción a todos los idiomas...')

    try {
      const spanishData = {
        name: editedValues.name ?? previewData.optimized.name,
        description: editedValues.description ?? previewData.optimized.description,
        additional_description: editedValues.additional_description ?? previewData.optimized.additional_description,
        link_rewrite: editedValues.link_rewrite ?? previewData.optimized.link_rewrite,
        meta_title: editedValues.meta_title ?? previewData.optimized.meta_title,
        meta_description: editedValues.meta_description ?? previewData.optimized.meta_description
      }

      setApplyProgress('Traduciendo a PT, EN, FR, DE y creando categoría...')

      const result = await categoryManagerApi.translateAndApply({
        category_id: undefined, // New category
        id_parent: selectedParentId,
        spanish_data: spanishData,
        json_data: previewData.json_data || {},
        model: aiModel,
        image_url: previewData.image_url
      })

      setApplyProgress(null)
      setApplyResult({
        success: result.success,
        message: result.message,
        category_id: result.category_id
      })

    } catch (error: any) {
      setApplyProgress(null)
      setApplyResult({
        success: false,
        message: error.response?.data?.detail || 'Error al crear la categoría'
      })
    } finally {
      setIsApplying(false)
    }
  }

  // Handle field edit
  const startEditing = (field: string) => {
    setEditingField(field)
  }

  const saveEdit = (field: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [field]: value }))
    setEditingField(null)
  }

  const cancelEdit = () => {
    setEditingField(null)
  }

  const getFieldValue = (field: string) => {
    return editedValues[field] ?? previewData?.optimized?.[field] ?? ''
  }

  const isFieldEdited = (field: string) => {
    return editedValues[field] !== undefined
  }

  // Reset form
  const resetForm = () => {
    setCategoryName('')
    setDescription('')
    setInstructions('')
    setPreviewData(null)
    setApplyResult(null)
    setEditedValues({})
    setGenerationError(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FolderPlus className="w-8 h-8 text-success-500" />
            Crear Categoría Web
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Describe la categoría y la IA generará todo el contenido optimizado para SEO
          </p>
        </div>
      </div>

      {/* Form Section */}
      {!previewData && !applyResult && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="space-y-6">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Nombre de la Categoría (ES)
              </label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                disabled={isGenerating}
                placeholder="Ej: Pantallas LCD 15.6 pulgadas"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-400"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Nombre que aparecerá en el menú de la web.
              </p>
            </div>

            {/* Parent Category Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Categoría Padre
              </label>
              <div className="relative">
                <select
                  value={selectedParentId}
                  onChange={(e) => setSelectedParentId(Number(e.target.value))}
                  disabled={loadingCategories || isGenerating}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white appearance-none pr-10"
                >
                  {loadingCategories ? (
                    <option>Cargando categorías...</option>
                  ) : (
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.display_name}
                      </option>
                    ))
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Description Textarea */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                ¿Qué es esta categoría?
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isGenerating}
                placeholder="Describe qué productos contendrá esta categoría. Por ejemplo: 'Pantallas LCD de portátiles de 15.6 pulgadas, incluyendo paneles HD y Full HD de fabricantes como LG, Samsung, BOE. Para reparación de portátiles.'"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-400 min-h-[150px] resize-y"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Describe el tipo de productos, características principales y público objetivo. Cuanto más detallado, mejor será el contenido generado.
              </p>
            </div>

            {/* Instructions Textarea */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                ¿Cómo quieres que escriba la IA? (opcional)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                disabled={isGenerating}
                placeholder="Ej: 'Usa un tono más técnico', 'Destaca la garantía de 2 años', 'Menciona el envío gratuito', 'Enfócate en profesionales del sector'..."
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-400 min-h-[80px] resize-y"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Instrucciones de estilo, tono o información específica que quieres incluir en el contenido.
              </p>
            </div>

            {/* Options Row */}
            <div className="flex flex-wrap gap-6">
              {/* AI Model Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Modelo IA
                </label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  disabled={isGenerating}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-4o-mini">GPT-4o Mini (Rápido)</option>
                    <option value="gpt-4o">GPT-4o (Mayor calidad)</option>
                  </optgroup>
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash (Muy rápido)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (Alta calidad)</option>
                  </optgroup>
                </select>
              </div>

              {/* Generate Image Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateImage}
                    onChange={(e) => setGenerateImage(e.target.checked)}
                    disabled={isGenerating}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    <ImageIcon className="w-4 h-4 inline mr-1" />
                    Generar imagen con DALL-E
                  </span>
                </label>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={!description.trim() || isGenerating}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                <Sparkles className="w-5 h-5" />
                {isGenerating ? 'Generando...' : 'Generar con IA'}
              </button>
            </div>
          </div>

          {/* Progress Indicator */}
          {isGenerating && (
            <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="font-medium text-blue-800 dark:text-blue-200">
                  {progress?.message || 'Iniciando generación...'}
                </span>
              </div>
              {progress && (
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
                La IA está generando el contenido optimizado para SEO{generateImage ? ' y la imagen de la categoría' : ''}.
              </p>
            </div>
          )}

          {/* Generation Error */}
          {generationError && (
            <div className="mt-6 p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2 text-danger-700 dark:text-red-300">
                <AlertCircle className="w-5 h-5" />
                <span>{generationError}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Section */}
      {previewData && !applyResult && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Check className="w-5 h-5 text-success-500" />
            Vista Previa de la Categoría
          </h2>

          {/* OpenAI Usage */}
          {previewData.openai_usage && (
            <div className="mb-6">
              <OpenAICostDisplay
                usage={previewData.openai_usage}
                showDetails={true}
              />
            </div>
          )}

          {/* Generated Image */}
          {previewData.image_url && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Imagen Generada
              </h3>
              <div className="flex items-start gap-4">
                <img
                  src={previewData.image_url}
                  alt="Imagen de categoría generada"
                  className="w-48 h-48 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Esta imagen se ha generado con DALL-E. Puedes guardarla manualmente y subirla a PrestaShop en img/c/{'{id_categoria}'}.jpg
                </p>
              </div>
            </div>
          )}

          {/* Editable Fields */}
          <div className="space-y-4">
            {/* Name */}
            <EditableField
              label="Nombre"
              field="name"
              value={getFieldValue('name')}
              isEditing={editingField === 'name'}
              isEdited={isFieldEdited('name')}
              onStartEdit={() => startEditing('name')}
              onSave={(value) => saveEdit('name', value)}
              onCancel={cancelEdit}
            />

            {/* Meta Title */}
            <EditableField
              label="Meta Título (SEO)"
              field="meta_title"
              value={getFieldValue('meta_title')}
              isEditing={editingField === 'meta_title'}
              isEdited={isFieldEdited('meta_title')}
              onStartEdit={() => startEditing('meta_title')}
              onSave={(value) => saveEdit('meta_title', value)}
              onCancel={cancelEdit}
              maxLength={60}
            />

            {/* Meta Description */}
            <EditableField
              label="Meta Descripción (SEO)"
              field="meta_description"
              value={getFieldValue('meta_description')}
              isEditing={editingField === 'meta_description'}
              isEdited={isFieldEdited('meta_description')}
              onStartEdit={() => startEditing('meta_description')}
              onSave={(value) => saveEdit('meta_description', value)}
              onCancel={cancelEdit}
              maxLength={160}
              showCharCount
            />

            {/* Link Rewrite */}
            <EditableField
              label="URL Amigable"
              field="link_rewrite"
              value={getFieldValue('link_rewrite')}
              isEditing={editingField === 'link_rewrite'}
              isEdited={isFieldEdited('link_rewrite')}
              onStartEdit={() => startEditing('link_rewrite')}
              onSave={(value) => saveEdit('link_rewrite', value)}
              onCancel={cancelEdit}
            />

            {/* Description (HTML) - Short, above products */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Descripción Corta (arriba de productos)
                  {isFieldEdited('description') && (
                    <span className="ml-2 text-xs text-primary-600">(editado)</span>
                  )}
                </span>
                {editingField !== 'description' && (
                  <button
                    onClick={() => startEditing('description')}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </button>
                )}
              </div>
              {editingField === 'description' ? (
                <div className="space-y-2">
                  <textarea
                    defaultValue={getFieldValue('description')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-h-[150px] font-mono text-sm"
                    id="edit-description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const el = document.getElementById('edit-description') as HTMLTextAreaElement
                        saveEdit('description', el.value)
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" /> Guardar
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="prose dark:prose-invert max-w-none text-sm bg-slate-50 dark:bg-slate-900 p-4 rounded-lg max-h-[200px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: getFieldValue('description') }}
                />
              )}
            </div>

            {/* Additional Description (HTML) - Long, below products */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Descripción Extensa (debajo de productos) - SEO
                  {isFieldEdited('additional_description') && (
                    <span className="ml-2 text-xs text-primary-600">(editado)</span>
                  )}
                </span>
                {editingField !== 'additional_description' && (
                  <button
                    onClick={() => startEditing('additional_description')}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </button>
                )}
              </div>
              {editingField === 'additional_description' ? (
                <div className="space-y-2">
                  <textarea
                    defaultValue={getFieldValue('additional_description')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-h-[300px] font-mono text-sm"
                    id="edit-additional-description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const el = document.getElementById('edit-additional-description') as HTMLTextAreaElement
                        saveEdit('additional_description', el.value)
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" /> Guardar
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="prose dark:prose-invert max-w-none text-sm bg-slate-50 dark:bg-slate-900 p-4 rounded-lg max-h-[400px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: getFieldValue('additional_description') }}
                />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6">
            {isApplying && applyProgress && (
              <div className="mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3">
                  <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="text-blue-800 dark:text-blue-200 font-medium">{applyProgress}</span>
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={resetForm}
                disabled={isApplying}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-50 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Descartar
              </button>
              <button
                onClick={handleApply}
                disabled={isApplying}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {isApplying ? 'Creando...' : 'Crear Categoría'}
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Al crear, el contenido se traducirá automáticamente a PT, EN, FR y DE.
            </p>
          </div>
        </div>
      )}

      {/* Success Result */}
      {applyResult && (
        <div className={`bg-white dark:bg-slate-800 rounded-lg shadow p-6`}>
          <div className={`p-4 rounded-lg ${
            applyResult.success
              ? 'bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700'
              : 'bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {applyResult.success ? (
                <Check className="w-5 h-5 text-success-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-danger-600" />
              )}
              <span className={applyResult.success ? 'text-green-700 dark:text-green-300' : 'text-danger-700 dark:text-red-300'}>
                {applyResult.message}
              </span>
            </div>

            {applyResult.success && applyResult.category_id && (
              <div className="mt-4">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  ID de la nueva categoría: <strong>{applyResult.category_id}</strong>
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2"
                  >
                    <FolderPlus className="w-4 h-4" />
                    Crear otra categoría
                  </button>
                  <a
                    href={`https://www.jvsinformatica.es/es/${getFieldValue('link_rewrite')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 flex items-center gap-2"
                  >
                    Ver en web
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Editable Field Component
interface EditableFieldProps {
  label: string
  field: string
  value: string
  isEditing: boolean
  isEdited: boolean
  onStartEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  maxLength?: number
  showCharCount?: boolean
}

function EditableField({
  label,
  field: _field,
  value,
  isEditing,
  isEdited,
  onStartEdit,
  onSave,
  onCancel,
  maxLength,
  showCharCount
}: EditableFieldProps) {
  const [editValue, setEditValue] = useState(value)

  useEffect(() => {
    setEditValue(value)
  }, [value, isEditing])

  if (isEditing) {
    return (
      <div className="border border-blue-300 dark:border-blue-600 rounded-lg p-3 bg-primary-50 dark:bg-primary-900/20">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {label}
        </label>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          maxLength={maxLength}
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          autoFocus
        />
        {showCharCount && (
          <div className="text-xs text-slate-500 mt-1">
            {editValue.length}{maxLength ? `/${maxLength}` : ''} caracteres
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onSave(editValue)}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
          >
            <Save className="w-3 h-3" /> Guardar
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded text-sm"
          >
            Cancelar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
          {isEdited && <span className="ml-2 text-xs text-primary-600">(editado)</span>}
        </span>
        <button
          onClick={onStartEdit}
          className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
        >
          <Pencil className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      <p className={`text-slate-900 dark:text-white mt-1 ${isEdited ? 'text-blue-700 dark:text-blue-300' : ''}`}>
        {value || <span className="text-slate-400 italic">Sin contenido</span>}
      </p>
      {showCharCount && (
        <div className="text-xs text-slate-500 mt-1">
          {value.length} caracteres
        </div>
      )}
    </div>
  )
}
